import {
    ChartBar,
    ClipboardText,
    Crown,
    EnvelopeSimple,
    Headset,
    Newspaper,
    Phone,
} from '@liveauctioneers/hammer-ui-core/icons';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { ReactNode } from 'react';

import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

import { Benefit, BenefitKey } from './types';

const VIP_SUPPORT_EMAIL = 'vipsupport@liveauctioneers.com';
const VIP_SUPPORT_PHONE = '(888)-600-2437';
const ONE_ON_ONE_URL = 'https://calendar.app.google/2yZL1HNxz6Ayj1LB8';
const ATG_SUBSCRIPTION_URL = 'https://news.antiquestradegazette.com/p/7T03-KO2/lavip?pfredir=1';

export const BENEFITS: Benefit[] = [
    { icon: EnvelopeSimple, key: 'priorityEmail' },
    { icon: Phone, key: 'priorityPhone' },
    { icon: Crown, key: 'vipStatus' },
    { icon: ChartBar, key: 'marketEstimates' },
    { icon: Headset, key: 'oneOnOneSessions' },
    { icon: Newspaper, key: 'atgSubscription' },
    { icon: ClipboardText, key: 'annualReport' },
];

export const BenefitItem = ({ benefit }: { benefit: Benefit }) => {
    const formatMessage = useAppFormatMessage();
    const Icon = benefit.icon;
    const descriptionValues: Partial<Record<BenefitKey, Record<string, ReactNode>>> = {
        atgSubscription: {
            subscribe: (
                <Link
                    href={ATG_SUBSCRIPTION_URL}
                    key="vip-atg-subscription"
                    rel="noopener noreferrer"
                    size="sm"
                    target="_blank"
                >
                    {formatMessage(['vipBenefits', 'subscribe'])}
                </Link>
            ),
        },
        oneOnOneSessions: {
            signUp: (
                <Link
                    href={ONE_ON_ONE_URL}
                    key="vip-one-on-one"
                    rel="noopener noreferrer"
                    size="sm"
                    target="_blank"
                >
                    {formatMessage(['vipBenefits', 'signUp'])}
                </Link>
            ),
        },
        priorityEmail: {
            email: (
                <Link
                    className="wrap-break-word"
                    href={`mailto:${VIP_SUPPORT_EMAIL}`}
                    key="vip-support-email"
                    size="sm"
                >
                    {VIP_SUPPORT_EMAIL}
                </Link>
            ),
        },
        priorityPhone: {
            phone: (
                <Link
                    href="tel:+18886002437"
                    key="vip-support-phone"
                    size="sm"
                >
                    {VIP_SUPPORT_PHONE}
                </Link>
            ),
        },
    };
    const description = formatMessage(['vipBenefits', 'benefits', benefit.key, 'description'], {
        values: descriptionValues[benefit.key],
    });

    return (
        <div className="flex w-full min-w-0 items-start gap-sm lgMin:px-0">
            <div className="shrink-0 rounded-md border border-border-secondary bg-background-primary p-xs">
                <Icon
                    color="secondary"
                    size="md"
                />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-3xs">
                <BodySecondary className="font-bold wrap-break-word">
                    {formatMessage(['vipBenefits', 'benefits', benefit.key, 'title'])}
                </BodySecondary>
                <BodySecondary className="wrap-break-word text-text-secondary">{description}</BodySecondary>
            </div>
        </div>
    );
};
